import React, { useEffect, useMemo, useState } from 'react';
import { CatalogueLite } from '../useMarketplaceState';
import { marketplaceService } from '../../services/marketplaceService';
import { detectVendorPreset, getComboKey } from '../vendorPresets';
import { CatalogueReorderView, type ReorderDomain, type ReorderProduct } from './CatalogueReorderView';

interface CataloguesTabProps {
  catalogues: CatalogueLite[];
  onCreate: () => void;
  onDuplicate: (snapshotId: string) => void;
  onArchive: (snapshotId: string) => void;
  onDelete?: (snapshotId: string) => void; // Dev only - permanently deletes catalogue
  onPublishToggle: (snapshotId: string, publish: boolean) => void;
  onAddProducts: (snapshotId: string, productIds: string[]) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  /** Callback to navigate to Products tab filtered to a specific product */
  onNavigateToProduct?: (productId: string, productName: string) => void;
}

interface CatalogueProduct {
  id: string;
  name: string;
  kind: 'software' | 'hardware' | 'consulting';
  vendorOrBrand: string;
  priceDisplay?: string;
  /** Numeric price + pricing type — used by the customer-preview modal. */
  price?: number;
  pricingType?: 'perUser' | 'lumpSum';
  enabled: boolean;
  category?: string;
  description?: string;
  image?: string;
  term?: number;
  billingCycle?: 'monthly' | 'annual';
  vendorPreset?: string;
  termPricing?: Record<string, number>;
  /** Lowercased domain — drives the reorder view's tab grouping. */
  domain?: ReorderDomain;
  /** Persisted display order; null/undefined sorts last. */
  displayOrder?: number;
  /** Extra fields the customer-preview modal needs. */
  features?: any;
  planInclusions?: any;
  enrichmentAttributes?: Record<string, unknown>;
  source?: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual';
  sku?: string;
  vendorPartNumber?: string;
  soh?: number;
  inclusionApps?: Array<{ name: string; image?: string }>;
}

const VALID_DOMAINS: ReorderDomain[] = [
  'computers', 'networking', 'mobile-hardware', 'unified-collaboration',
  'telstra', 'software', 'consulting',
];

function normaliseDomain(raw: unknown, fallback: ReorderDomain): ReorderDomain {
  const v = String(raw ?? '').toLowerCase().trim();
  return (VALID_DOMAINS as readonly string[]).includes(v) ? (v as ReorderDomain) : fallback;
}

// Removed SubView type as we no longer need assignments sub-view


// Assignments functionality has been moved to the customer-specific Marketplace view in TenantsView

// Check if we're in development mode
const isDev = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname.includes('dev') ||
  window.location.hostname.includes('green-coast')
);

export function CataloguesTab(props: CataloguesTabProps) {
  const { catalogues, onCreate, onDuplicate, onArchive, onDelete, onPublishToggle, onAddProducts } = props;
  const [selectedId, setSelectedId] = useState<string | null>(catalogues[0]?.snapshotId || null);
  const [search, setSearch] = useState('');
  
  const visible = useMemo(() => catalogues.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())), [catalogues, search]);
  const selected = useMemo(() => visible.find((c) => c.snapshotId === selectedId) || visible[0] || null, [visible, selectedId]);
  const [products, setProducts] = useState<CatalogueProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  
  // Catalogue name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadProducts() {
      if (!selected) { setProducts([]); return; }
      setLoadingProducts(true);
      try {
        const snapshot = await marketplaceService.getPartnerSnapshotBlob(selected.snapshotId);
        const formatPrice = (price: number) => price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const sw: CatalogueProduct[] = (snapshot.software || []).map((s: any) => {
          const preset = detectVendorPreset(s.vendor);
          let tp = s.termPricing as Record<string, number> | undefined;
          if (preset && !tp) {
            const basePrice = typeof s.price === 'number' ? s.price : 0;
            tp = {};
            for (const combo of preset.validCombinations) {
              tp[getComboKey(combo.billingCycle, combo.term)] = basePrice;
            }
          }
          let priceDisplay: string;
          if (preset && tp) {
            const m2mPrice = tp[getComboKey('monthly', 0)];
            priceDisplay = typeof m2mPrice === 'number' && m2mPrice > 0
              ? `$${formatPrice(m2mPrice)}/user (M2M)`
              : (s.price && s.price > 0 ? `$${formatPrice(s.price)}/user` : '-');
          } else {
            priceDisplay = s.price && s.price > 0
              ? (s.pricingType === 'perUser' ? `$${formatPrice(s.price)}/user` : `$${formatPrice(s.price)}`)
              : '-';
          }
          return {
            id: s.id,
            name: s.name,
            kind: 'software' as const,
            vendorOrBrand: preset?.label || s.vendor || 'Unknown',
            priceDisplay,
            price: typeof s.price === 'number' ? s.price : undefined,
            pricingType: s.pricingType,
            enabled: s.enabled !== false,
            category: s.category,
            description: s.description,
            image: s.image,
            term: s.term,
            billingCycle: s.billingCycle,
            vendorPreset: preset?.id,
            termPricing: tp,
            domain: normaliseDomain(s.domain, 'software'),
            displayOrder: typeof s.displayOrder === 'number' ? s.displayOrder : undefined,
            features: s.features,
            planInclusions: s.planInclusions,
            inclusionApps: s.inclusionApps,
            source: s.source,
            sku: s.sku,
            vendorPartNumber: (s as any).vendorPartNumber,
          };
        });
        const hw: CatalogueProduct[] = (snapshot.hardware || []).map((h: any) => ({
          id: h.id,
          name: h.name,
          kind: 'hardware' as const,
          vendorOrBrand: h.brand || 'Unknown',
          priceDisplay: h.price > 0 ? `$${formatPrice(h.price)}` : '-',
          price: typeof h.price === 'number' ? h.price : undefined,
          pricingType: 'lumpSum' as const,
          enabled: h.enabled !== false,
          category: h.category,
          description: h.description,
          image: h.image,
          domain: normaliseDomain(h.domain, 'computers'),
          displayOrder: typeof h.displayOrder === 'number' ? h.displayOrder : undefined,
          features: h.features,
          planInclusions: h.planInclusions,
          enrichmentAttributes: h.enrichmentAttributes,
          source: h.source,
          sku: h.sku,
          vendorPartNumber: h.vendorPartNumber,
          soh: h.soh,
        }));
        const cs: CatalogueProduct[] = ((snapshot as any).consulting || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          kind: 'consulting' as const,
          vendorOrBrand: c.brand || 'Service Provider',
          priceDisplay: c.price > 0 ? `$${formatPrice(c.price)}` : '-',
          price: typeof c.price === 'number' ? c.price : undefined,
          pricingType: 'lumpSum' as const,
          enabled: c.enabled !== false,
          category: c.category || 'consulting',
          description: c.description,
          image: c.image,
          domain: 'consulting' as ReorderDomain,
          displayOrder: typeof c.displayOrder === 'number' ? c.displayOrder : undefined,
        }));
        if (!cancelled) setProducts([...sw, ...hw, ...cs]);
      } catch (e) {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }
    loadProducts();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, reloadKey]);

  const handleNameEdit = () => {
    if (!selected) return;
    setEditedName(selected.name);
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (!selected || !editedName.trim()) return;
    try {
      const snapshot = await marketplaceService.getPartnerSnapshotBlob(selected.snapshotId);
      await marketplaceService.createPartnerSnapshotBlob({
        snapshotId: selected.snapshotId,
        name: editedName.trim(),
        notes: (snapshot as any).notes || '',
        software: snapshot.software || [],
        hardware: snapshot.hardware || [],
        consulting: (snapshot as any).consulting || []
      });
      // Update local state instead of reloading page
      selected.name = editedName.trim();
      setIsEditingName(false);
    } catch (e) {
      // Error renaming catalogue
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  return (
    <div className="flex h-full">

      {/* Catalogue List Sidebar */}
      <div className="w-72 border-r border-gray-200 bg-white p-3 flex-shrink-0">
        <div className="mb-2">
          <button onClick={onCreate} className="w-full inline-flex items-center justify-center px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700">New Catalogue</button>
        </div>
        <div className="mb-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search catalogues" className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="overflow-auto h-[calc(100%-88px)]">
          <ul className="space-y-1">
            {visible.map((c) => (
              <li key={c.snapshotId}>
                <button 
                  onClick={() => setSelectedId(c.snapshotId)} 
                  className={`w-full text-left px-3 py-2 rounded-md border ${
                    selected?.snapshotId === c.snapshotId ? 'bg-gray-50 border-gray-200' : 'border-transparent hover:bg-gray-50'
                  } ${c.name === 'All Products' ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  <div className={`text-sm font-medium truncate flex items-center gap-2 ${
                    c.name === 'All Products' ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {c.name === 'All Products' && (
                      <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M19 11H5m14 0a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2m14 0V9a2 2 0 0 0-2-2M5 11V9a2 2 0 0 1 2-2m0 0V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2M7 7h10"/>
                      </svg>
                    )}
                    <span>{c.name}</span>
                    {c.name === 'All Products' && (
                      <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">DEFAULT</span>
                    )}
                  </div>
                  <div className={`text-xs ${c.name === 'All Products' ? 'text-blue-700' : 'text-gray-500'}`}>
                    {c.itemCount} items • {c.status === 'published' ? 'Published' : 'Draft'}
                  </div>
                </button>
              </li>
            ))}
            {visible.length === 0 && <li className="text-sm text-gray-500 px-3 py-2">No catalogues</li>}
          </ul>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col h-full">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="text-gray-900 font-medium mb-1">Create your first catalogue</div>
              <div className="text-sm text-gray-600 mb-4">Bundle products and publish when ready</div>
              <button onClick={onCreate} className="inline-flex items-center px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700">New Catalogue</button>
            </div>
          </div>
        ) : (
          <>
            {/* Catalogue Header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between group">
                <div className="flex-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="text-lg font-semibold bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleNameSave();
                          if (e.key === 'Escape') handleNameCancel();
                        }}
                        autoFocus
                        aria-label="Catalogue name"
                      />
                      <button
                        onClick={handleNameSave}
                        className="px-2 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        title="Save name"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleNameCancel}
                        className="px-2 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                        title="Cancel"
                      >
                        ✗
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className={`text-lg font-semibold ${selected.name === 'All Products' ? 'text-blue-900' : 'text-gray-900'}`}>
                        {selected.name}
                      </h3>
                      {selected.name === 'All Products' && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">DEFAULT</span>
                      )}
                      {selected.name !== 'All Products' && (
                        <button
                          onClick={handleNameEdit}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Edit catalogue name"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">{selected.itemCount} items{selected.createdAtUtc ? ` • Created ${new Date(selected.createdAtUtc).toLocaleDateString()}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {selected.name !== 'All Products' && (
                    <button onClick={() => onAddProducts(selected.snapshotId, [])} className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add Products
                    </button>
                  )}
                  {selected.name !== 'All Products' && (
                    <>
                      <button onClick={() => onDuplicate(selected.snapshotId)} className="px-3 py-1.5 rounded-md border border-gray-200 text-sm hover:bg-gray-50">Duplicate</button>
                      <button onClick={() => onPublishToggle(selected.snapshotId, selected.status !== 'published')} className={`px-3 py-1.5 rounded-md text-sm ${selected.status === 'published' ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                        {selected.status === 'published' ? 'Unpublish' : 'Publish'}
                      </button>
                      <button onClick={() => onArchive(selected.snapshotId)} className="px-3 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700">Archive</button>
                      {isDev && onDelete && (
                        <button 
                          onClick={() => onDelete(selected.snapshotId)} 
                          className="px-3 py-1.5 rounded-md bg-red-800 text-white text-sm hover:bg-red-900 border-2 border-red-400"
                          title="DEV ONLY: Permanently delete catalogue"
                        >
                          🗑️ Delete (DEV)
                        </button>
                      )}
                    </>
                  )}
                  {selected.name === 'All Products' && (
                    <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                      <svg className="w-4 h-4 inline-block mr-2 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M13 16h-1v-4h-1m1-4h.01"/>
                        <circle cx="12" cy="12" r="10"/>
                      </svg>
                      Default catalogue - contains all your products
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Drag-and-drop reorder view (replaces the legacy filters + table). */}
            {loadingProducts ? (
              <div className="flex-1 flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
                <span className="ml-3 text-slate-600">Loading products...</span>
              </div>
            ) : (
              <CatalogueReorderView
                snapshotId={selected.snapshotId}
                catalogueName={selected.name}
                products={products
                  .filter((p) => p.domain)
                  .map<ReorderProduct>((p) => ({
                    id: p.id,
                    name: p.name,
                    vendorOrBrand: p.vendorOrBrand,
                    kind: p.kind,
                    domain: p.domain as ReorderDomain,
                    priceDisplay: p.priceDisplay,
                    price: p.price,
                    pricingType: p.pricingType,
                    billingCycle: p.billingCycle,
                    image: p.image,
                    enabled: p.enabled,
                    displayOrder: p.displayOrder,
                    description: p.description,
                    features: p.features,
                    planInclusions: p.planInclusions,
                    enrichmentAttributes: p.enrichmentAttributes,
                    source: p.source,
                    sku: p.sku,
                    vendorPartNumber: p.vendorPartNumber,
                    soh: p.soh,
                    vendorPreset: p.vendorPreset,
                    inclusionApps: p.inclusionApps,
                  }))}
                onSaved={() => setReloadKey((k) => k + 1)}
                getCachedImageUrl={props.getCachedImageUrl}
                isAllProducts={selected.name === 'All Products'}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CataloguesTab;
